import React, { useEffect, useMemo, useState } from "react";
import { imgBaseUrl } from "../../api/api";
import { formatToDateFull } from "../master/FormatToDateTime";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const GRADE_SCALE = [
  { grade: "A1", min: 86, max: 100 },
  { grade: "A2", min: 76, max: 85 },
  { grade: "B1", min: 60, max: 75 },
  { grade: "B2", min: 50, max: 59 },
  { grade: "C", min: 40, max: 49 },
  { grade: "D", min: 30, max: 39 },
  { grade: "E", min: 0, max: 29 },
];

const getGrade = (pct) => GRADE_SCALE.find((g) => pct >= g.min)?.grade ?? "E";

const getDivision = (pct) => {
  if (pct >= 60) return "First";
  if (pct >= 50) return "Second";
  if (pct >= 33) return "Third";
  return "Fail";
};

const getRemark = (pct) => {
  if (pct >= 90) return "Outstanding performance";
  if (pct >= 80) return "Excellent performance. Keep it up";
  if (pct >= 70) return "Has shown good growth in academics";
  if (pct >= 50) return "Satisfactory performance";
  if (pct >= 33) return "Needs more attention";
  return "Requires special attention";
};

const safeParse = (value, fallback = null) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return fallback;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const classes = [
  "NUR.",
  "LKG.",
  "UKG.",
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
  "IX",
  "X",
  "XI",
  "11 ARTS",
  "XII ARTS",
];
const nextClass = (current) => {
  const i = classes.indexOf(current);
  return i === -1 || i === classes.length - 1 ? null : classes[i + 1];
};

// Every test of every series, in order
const getTestColumns = (student) => {
  const cols = [];
  student?.student_info?.series?.forEach((series) => {
    series?.tests?.forEach((test) => {
      cols.push({
        key: `${series.series_id}-${test.test_id}`,
        seriesId: series.series_id,
        seriesName: series.series_name,
        test,
      });
    });
  });
  return cols;
};

/**
 * Subjects this student actually has in one test.
 * marks.subject_ids is the source of truth, subjects_detail is the fallback.
 * status: "ok" | "absent" | "na" (marks not entered yet)
 */
const getStudentTestSubjects = (test, batch) => {
  const detail = test?.subjects_detail ?? [];
  const m = test?.marks;
  const ids =
    safeParse(m?.subject_ids, null) ?? detail.map((d) => String(d.id));
  const maxes = safeParse(m?.subjects_marks, []);
  const obtained = safeParse(m?.obtained_marks, null);
  const testAbsent = String(m?.is_absent) === "1";

  const result = {};
  ids.forEach((rawId, idx) => {
    const id = Number(rawId);
    const d = detail.find((x) => Number(x.id) === id);
    const name =
      d?.name ??
      batch?.course?.subjects_list?.find((s) => Number(s.id) === id)?.name ??
      `Subject ${id}`;
    const max = Number(maxes?.[idx] ?? d?.marks ?? 0);

    let status = "ok";
    let value = 0;
    const raw = obtained ? obtained[idx] : undefined;

    if (testAbsent) status = "absent";
    else if (!obtained || raw === undefined || raw === null || raw === "")
      status = "na";
    else if (isNaN(Number(raw)))
      status = "absent"; // e.g. "A"
    else value = Number(raw);

    result[id] = { name, max, obtained: value, status };
  });
  return result;
};

const computeStats = (student, batch) => {
  const columns = getTestColumns(student).map((c) => ({
    ...c,
    subjects: getStudentTestSubjects(c.test, batch),
  }));

  // Union of the student's subjects across tests, in course order
  const present = new Set();
  columns.forEach((c) =>
    Object.keys(c.subjects).forEach((id) => present.add(Number(id))),
  );
  const subjects = [];
  batch?.course?.subjects_list?.forEach((s) => {
    if (present.has(Number(s.id)))
      subjects.push({ id: Number(s.id), name: s.name });
  });
  columns.forEach((c) =>
    Object.entries(c.subjects).forEach(([id, s]) => {
      if (!subjects.find((o) => o.id === Number(id)))
        subjects.push({ id: Number(id), name: s.name });
    }),
  );

  // "na" cells are ignored in totals; "absent" counts 0 out of max
  const counted = (cell) => cell && cell.status !== "na";

  // Header max for a test: shown only when every subject has the same max
  columns.forEach((c) => {
    const maxSet = new Set(Object.values(c.subjects).map((s) => s.max));
    c.uniformMax = maxSet.size === 1 ? [...maxSet][0] : null;
  });

  const subjectTotals = {};
  subjects.forEach((sub) => {
    let obtained = 0;
    let max = 0;
    columns.forEach((c) => {
      const cell = c.subjects[sub.id];
      if (counted(cell)) {
        obtained += cell.obtained;
        max += cell.max;
      }
    });
    subjectTotals[sub.id] = {
      obtained,
      max,
      pct: max ? (obtained / max) * 100 : 0,
      hasData: max > 0,
    };
  });

  const testTotals = columns.map((c) => {
    let obtained = 0;
    let max = 0;
    Object.values(c.subjects).forEach((cell) => {
      if (counted(cell)) {
        obtained += cell.obtained;
        max += cell.max;
      }
    });
    return {
      obtained,
      max,
      pct: max ? (obtained / max) * 100 : 0,
      hasData: max > 0,
    };
  });

  const obtained = testTotals.reduce((s, t) => s + t.obtained, 0);
  const max = testTotals.reduce((s, t) => s + t.max, 0);

  // Total column header shows a max only when all subjects share the same total max
  const totalMaxSet = new Set(
    Object.values(subjectTotals)
      .filter((t) => t.hasData)
      .map((t) => t.max),
  );

  return {
    columns,
    subjects,
    subjectTotals,
    testTotals,
    obtained,
    max,
    pct: max ? (obtained / max) * 100 : 0,
    uniformTotalMax: totalMaxSet.size === 1 ? [...totalMaxSet][0] : null,
  };
};

/* ------------------------------------------------------------------ */
/* Styles (self-contained, no dependency on the old marksheet CSS)     */
/* ------------------------------------------------------------------ */

const css = (logo) => `
.ms7 {
  width: 210mm; height: 290mm; box-sizing: border-box;
  padding: 7mm 8mm; position: relative; overflow: hidden;
  background: #fff; color: #111; font-family: "Segoe UI", Arial, sans-serif;
  font-size: 12px; line-height: 1.35;
  border: 2pt solid #1b2a5c;
  -webkit-print-color-adjust: exact; print-color-adjust: exact;
  page-break-inside: avoid;
}
.ms7 *, .ms7 *::before, .ms7 *::after { box-sizing: border-box; }
.ms7::before {
  content: ""; position: absolute; inset: 0; z-index: 0;
  background: url(${logo}) center 55% / 300px no-repeat; opacity: .07;
}
.ms7 > * { position: relative; z-index: 1; }

.ms7-head { display: flex; align-items: center; gap: 14px; padding-bottom: 8px;
  border-bottom: 2.5pt solid #1b2a5c; }
.ms7-head img.logo { width: 82px; height: 82px; object-fit: contain; }
.ms7-head .txt { flex: 1; text-align: center; }
.ms7-head h1 { margin: 0; font-size: 26px; font-weight: 800; letter-spacing: .5px; color: #1b2a5c; text-transform: uppercase; }
.ms7-head p { margin: 1px 0 0; font-size: 12px; }
.ms7-banner img { width: 100%; display: block; }

.ms7-title { margin: 8px auto 6px; width: max-content; padding: 3px 28px;
  background: #1b2a5c; color: #fff; font-weight: 700; letter-spacing: 1.5px;
  border-radius: 20px; text-transform: uppercase; font-size: 13px; }
.ms7-session { text-align: center; font-weight: 700; margin-bottom: 6px; }

.ms7-info { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
.ms7-info td { border: .75pt solid #9aa3bf; padding: 6px 8px; }
.ms7-info td.l { background: #eef1fa; font-weight: 700; width: 17%; white-space: nowrap; }
.ms7-info td.v { width: 33%; text-transform: uppercase; }

.ms7-marks { width: 100%; border-collapse: collapse; table-layout: fixed; margin-bottom: 14px; }
.ms7-marks th, .ms7-marks td { border: 1pt solid #111; text-align: center; padding: 10px 4px; }
.ms7-marks thead th { background: #dfe6fa; vertical-align: middle; }
.ms7-marks th .tn { display: block; font-weight: 800; text-transform: uppercase; }
.ms7-marks th .sn { display: block; font-size: 9px; font-weight: 600; color: #444; text-transform: uppercase; }
.ms7-marks th .mx { display: block; font-weight: 600; font-size: 11px; }
.ms7-marks td.sub { text-align: left; padding-left: 8px; font-weight: 700; text-transform: uppercase; }
.ms7-marks th.sub { width: 20%; text-align: left; padding-left: 8px; }
.ms7-marks td.tot, .ms7-marks th.tot { background: #f1f4fc; font-weight: 800; }
.ms7-marks tr.total td { background: #dfe6fa; font-weight: 800; }
.ms7-marks tr.pct td { font-weight: 700; }
.ms7-marks .of { opacity: .65; font-size: .85em; }
.ms7-marks.dense th, .ms7-marks.dense td { font-size: 10px; padding: 5px 2px; }

.ms7-sum { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
.ms7-sum th, .ms7-sum td { border: 1pt solid #111; text-align: center; padding: 4px; }
.ms7-sum th { background: #dfe6fa; text-transform: uppercase; font-size: 11px; }
.ms7-sum td { font-weight: 800; font-size: 14px; height: 40px; }

.ms7-foot { border: 1pt solid #111; padding: 12px 14px; }
.ms7-line { display: flex; gap: 6px; align-items: flex-end; }
.ms7-line .fill { flex: 1; border-bottom: .75pt solid #111; min-height: 16px; padding: 0 4px; }
.ms7-sign { display: flex; gap: 60px; margin-top: 10px; }
.ms7-sign .box { flex: 1; text-align: center; }
.ms7-sign .sigspace { height: 70px; display: flex; align-items: flex-end; justify-content: center; }
.ms7-sign .sigspace img { max-height: 66px; max-width: 80%; object-fit: contain; }
.ms7-sign .cap { border-top: .75pt solid #111; padding-top: 4px; font-weight: 700; text-transform: uppercase; }

.ms7-scale { margin-top: 14px; font-size: 10px; }
.ms7-scale table { width: 100%; border-collapse: collapse; margin-top: 3px; }
.ms7-scale th, .ms7-scale td { border: .75pt solid #111; text-align: center; padding: 2px; font-size: 10px; }
.ms7-scale th { background: #dfe6fa; }
.ms7-scale .legend { text-align: center; }
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function StudentMarksheet7({ data, batch, examDate }) {
  const [setting, setSetting] = useState({});

  useEffect(() => {
    setSetting(JSON.parse(localStorage.getItem("setting") ?? "{}"));
  }, []);

  const stats = useMemo(() => computeStats(data, batch), [data, batch]);

  // Rank inside the batch by overall percentage (ties share a rank)
  const rank = useMemo(() => {
    if (!stats.max) return "";
    const pcts = (batch?.active_batch_students ?? [])
      .map((s) => computeStats(s, batch))
      .filter((s) => s.max > 0)
      .map((s) => s.pct);
    return pcts.filter((p) => p > stats.pct + 1e-9).length + 1;
  }, [batch, stats]);

  const multiSeries = new Set(stats.columns.map((c) => c.seriesId)).size > 1;
  const dense = stats.columns.length > 5;
  const passed = stats.pct >= 33;
  const info = data?.student_info;

  // Principal signature image from settings (same way as logo_img).
  // Adjust the key if your setting uses a different name.
  const signature =
    setting?.signature ??
    setting?.signature_img ??
    setting?.principal_signature ??
    setting?.sign_img;

  // Cell content. Max is shown in the header when uniform, otherwise per cell.
  const renderCell = (cell, uniformMax) => {
    if (!cell) return "—";
    if (cell.status === "na") return "-";
    if (cell.status === "absent") return "AB";
    return uniformMax !== null ? (
      cell.obtained
    ) : (
      <>
        {cell.obtained}
        <span className="of"> / {cell.max}</span>
      </>
    );
  };

  const InfoRow = ({ a, b }) => (
    <tr>
      <td className="l">{a[0]}</td>
      <td className="v">{a[1]}</td>
      <td className="l">{b[0]}</td>
      <td className="v">{b[1]}</td>
    </tr>
  );

  return (
    <div className="ms7">
      <style>{css(imgBaseUrl + (setting?.logo_img ?? ""))}</style>

      {/* ---------------- Header ---------------- */}
      {setting?.student_maksheet_header_logo ? (
        <div className="ms7-banner">
          <img src={imgBaseUrl + setting.student_maksheet_header_logo} alt="" />
        </div>
      ) : (
        <div className="ms7-head">
          <img className="logo" src={imgBaseUrl + setting?.logo_img} alt="" />
          <div className="txt">
            <h1>{setting?.name}</h1>
            <p>
              {setting?.village} {setting?.district} {setting?.state}{" "}
              {setting?.pin_code}
            </p>
            <p>
              Mobile : {setting?.mobile}
              {setting?.secondary_mobile && `, ${setting.secondary_mobile}`}
              {setting?.email && ` | Email : ${setting.email}`}
            </p>
          </div>
        </div>
      )}

      <div className="ms7-title">Student Report Card</div>
      <div className="ms7-session">
        Academic Session : {batch?.sessions?.session}
      </div>

      {/* ---------------- Student details ---------------- */}
      <table className="ms7-info">
        <tbody>
          <InfoRow
            a={["Student Name", info?.name]}
            b={["Father's Name", info?.father_name]}
          />
          <InfoRow
            a={["Mother's Name", info?.mother_name]}
            b={["Date of Birth", formatToDateFull(info?.dob)]}
          />
          <InfoRow
            a={["Class", batch?.course?.title]}
            b={["Section", batch?.title]}
          />
          <InfoRow
            a={["SR. No", info?.registration_no]}
            b={["Roll No", data?.roll_number]}
          />
          <InfoRow
            a={["Apaar ID", info?.apaar_id]}
            b={["NIC ID", info?.nic_id]}
          />
        </tbody>
      </table>

      {/* ---------------- Marks table ---------------- */}
      <table className={`ms7-marks ${dense ? "dense" : ""}`}>
        <thead>
          <tr>
            <th className="sub">Subject</th>
            {stats.columns.map((c) => (
              <th key={c.key}>
                {multiSeries && <span className="sn">{c.seriesName}</span>}
                <span className="tn">{c.test.test_name}</span>
                {c.uniformMax !== null && (
                  <span className="mx">({c.uniformMax})</span>
                )}
              </th>
            ))}
            <th className="tot">
              <span className="tn">Total</span>
              {stats.uniformTotalMax !== null && (
                <span className="mx">({stats.uniformTotalMax})</span>
              )}
            </th>
            <th className="tot" style={{ width: "9%" }}>
              <span className="tn">Grade</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {stats.subjects.map((sub) => {
            const t = stats.subjectTotals[sub.id];
            return (
              <tr key={sub.id}>
                <td className="sub">{sub.name}</td>
                {stats.columns.map((c) => (
                  <td key={c.key}>
                    {renderCell(c.subjects[sub.id], c.uniformMax)}
                  </td>
                ))}
                <td className="tot">
                  {t.hasData ? (
                    stats.uniformTotalMax !== null ? (
                      t.obtained
                    ) : (
                      <>
                        {t.obtained}
                        <span className="of"> / {t.max}</span>
                      </>
                    )
                  ) : (
                    "-"
                  )}
                </td>
                <td className="tot">{t.hasData ? getGrade(t.pct) : "-"}</td>
              </tr>
            );
          })}

          <tr className="total">
            <td className="sub">Total</td>
            {stats.testTotals.map((t, i) => (
              <td key={i}>
                {t.hasData ? (
                  <>
                    {t.obtained}
                    <span className="of"> / {t.max}</span>
                  </>
                ) : (
                  "-"
                )}
              </td>
            ))}
            <td>
              {stats.max ? (
                <>
                  {stats.obtained}
                  <span className="of"> / {stats.max}</span>
                </>
              ) : (
                "-"
              )}
            </td>
            <td>{stats.max ? getGrade(stats.pct) : "-"}</td>
          </tr>

          <tr className="pct">
            <td className="sub">Percentage</td>
            {stats.testTotals.map((t, i) => (
              <td key={i}>{t.hasData ? `${t.pct.toFixed(1)}%` : "-"}</td>
            ))}
            <td colSpan={2}>{stats.max ? `${stats.pct.toFixed(2)}%` : "-"}</td>
          </tr>
        </tbody>
      </table>

      {/* ---------------- Overall result ---------------- */}
      <table className="ms7-sum">
        <thead>
          <tr>
            <th>Grand Total</th>
            <th>Percentage</th>
            <th>Grade</th>
            <th>Division</th>
            <th>Rank</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{stats.max ? `${stats.obtained} / ${stats.max}` : "-"}</td>
            <td>{stats.max ? `${stats.pct.toFixed(2)}%` : "-"}</td>
            <td>{stats.max ? getGrade(stats.pct) : "-"}</td>
            <td>{stats.max ? getDivision(stats.pct) : "-"}</td>
            <td>{rank || "-"}</td>
            <td>{stats.max ? (passed ? "Passed" : "Failed") : "-"}</td>
          </tr>
        </tbody>
      </table>

      {/* ---------------- Remarks & signatures ---------------- */}
      <div className="ms7-foot">
        <div className="ms7-line">
          <span>Class teacher's remark :</span>
          <span className="fill">{stats.max ? getRemark(stats.pct) : ""}</span>
          <span style={{ marginLeft: 12 }}>Promoted to class :</span>
          <span className="fill" style={{ maxWidth: 90 }}>
            {passed && stats.max ? nextClass(batch?.course?.title) : ""}
          </span>
        </div>
        <div style={{ marginTop: 6 }}>Date : {formatToDateFull(examDate)}</div>
        <div className="ms7-sign">
          <div className="box">
            <div className="sigspace"></div>
            <div className="cap">Class Teacher</div>
          </div>
          <div className="box">
            <div className="sigspace">
              {signature && (
                <img src={imgBaseUrl + signature} alt="Principal signature" />
              )}
            </div>
            <div className="cap">Principal</div>
          </div>
        </div>
      </div>

      {/* ---------------- Grading scale ---------------- */}
      <div className="ms7-scale">
        <div className="legend">
          <b>Grading :</b> AB = Absent &nbsp;|&nbsp; "-" = marks not entered
          &nbsp;|&nbsp; "—" = subject not in this test
        </div>
        <table>
          <thead>
            <tr>
              {GRADE_SCALE.map((g) => (
                <th key={g.grade}>{g.grade}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {GRADE_SCALE.map((g) => (
                <td key={g.grade}>
                  {g.min} - {g.max}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
